/**
 * @fileoverview Connect recorded agent-run detail to evidence inspection and explicit staff feedback. Detail and feedback hooks own their request/modal state; `IntakeAgentOutput.js` owns structured findings/citation presentation. The backend retains run/prompt/model provenance and feedback history. Opening an old run reads its recorded context rather than rerunning the current agent configuration.
 */

import { useCallback, useContext, useState } from 'react';
import { AppModal } from '../shared';
import AppIcon from '../shared/AppIcon';
import ToastContext from '../../ToastContext';
import { EmptyState, IntakeBadge, TextArea } from './IntakeDetailPrimitives';
import {
  createIntakeAgentRunFeedback,
  fetchIntakeAgentRunDetail,
  fetchIntakeAgentRunFeedback,
  updateIntakeFeedback,
} from './intakeApi';
import { formatDate, getActivityDetail, titleize } from './intakeFormatters';
import { formatJsonBlock, FormattedAgentOutput, LegalSourceDetailModal } from './IntakeAgentOutput';

// Agent run detail/context and feedback modals plus the hooks that load their data.
// Used by the pipeline stage timeline and the Run Logs tab.



export function useAgentRunDetailModal() {
  const showToast = useContext(ToastContext);
  const [detail, setDetail] = useState(null);
  const [loadingRunId, setLoadingRunId] = useState(null);

  const openAgentRunDetail = useCallback(

  async (leadId, runId) => {
    if (!leadId || !runId) return;
    setLoadingRunId(runId);
    try {
      const detailResponse = await fetchIntakeAgentRunDetail(leadId, runId);
      setDetail(detailResponse.data.agent_run);
    } catch (error) {
      console.error('Failed to load intake agent run detail:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load run details' });
    } finally {
      setLoadingRunId(null);
    }
  }, [showToast]);

  const closeAgentRunDetail = useCallback(

  () => {
    setDetail(null);
  }, []);

  return {
    detail,
    loadingRunId,
    openAgentRunDetail,
    closeAgentRunDetail
  };
}



export function useAgentFeedbackModal() {
  const showToast = useContext(ToastContext);
  const [target, setTarget] = useState(null);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingRunId, setLoadingRunId] = useState(null);

  const openAgentFeedback = useCallback(

  async (run) => {
    if (!run?.lead_id || !run?.id) return;
    setTarget(run);
    setFeedbackItems([]);
    setLoading(true);
    setLoadingRunId(run.id);
    try {
      const response = await fetchIntakeAgentRunFeedback(run.lead_id, run.id);
      setFeedbackItems(response.data.feedback || []);
    } catch (error) {
      console.error('Failed to load intake agent feedback:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to load agent feedback' });
    } finally {
      setLoading(false);
      setLoadingRunId(null);
    }
  }, [showToast]);

  const submitAgentFeedback = useCallback(

  async (payload) => {
    if (!target?.lead_id || !target?.id) return false;
    setSaving(true);
    try {
      const response = await createIntakeAgentRunFeedback(target.lead_id, target.id, payload);
      setFeedbackItems(response.data.feedback_items || []);
      showToast({ severity: 'success', summary: 'Saved', detail: 'Agent feedback saved' });
      return true;
    } catch (error) {
      console.error('Failed to save intake agent feedback:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to save agent feedback' });
      return false;
    } finally {
      setSaving(false);
    }
  }, [target, showToast]);

  const deactivateAgentFeedback = useCallback(

  async (feedbackId) => {
    if (!feedbackId) return;
    setSaving(true);
    try {
      const response = await updateIntakeFeedback(feedbackId, {
        status: 'inactive',
        reason: 'Deactivated from agent feedback modal'
      });
      const updated = response.data.feedback;
      setFeedbackItems((current) => current.map((item) => item.id === updated.id ? updated : item));
      showToast({ severity: 'success', summary: 'Updated', detail: 'Feedback deactivated' });
    } catch (error) {
      console.error('Failed to deactivate intake agent feedback:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to deactivate feedback' });
    } finally {
      setSaving(false);
    }
  }, [showToast]);

  const closeAgentFeedback = useCallback(

  () => {
    setTarget(null);
    setFeedbackItems([]);
  }, []);

  return {
    target,
    feedbackItems,
    loading,
    saving,
    loadingRunId,
    openAgentFeedback,
    closeAgentFeedback,
    submitAgentFeedback,
    deactivateAgentFeedback
  };
}



function getDetailVersionLabel(prefix, id, number) {
  if (number) return `${prefix} v${number}`;
  if (id) return `${prefix} #${id}`;
  return '-';
}



function AgentRunMetadataGrid({ detail }) {
  return (
    <div className="intake-stage-field-grid">
      <div className="intake-stage-field"><span>Pipeline Version</span><strong>{getDetailVersionLabel('Pipeline', detail.pipeline_version_id, detail.pipeline_version_number)}</strong></div>
      <div className="intake-stage-field"><span>Prompt Version</span><strong>{getDetailVersionLabel('Prompt', detail.prompt_version_id, detail.prompt_version_number)}</strong></div>
      <div className="intake-stage-field"><span>Stage</span><strong>{detail.stage_label || detail.stage_key || detail.agent_stage_key || '-'}</strong></div>
      <div className="intake-stage-field"><span>Agent</span><strong>{detail.agent_display_name || detail.agent_key || `Agent #${detail.agent_id || '-'}`}</strong></div>
      <div className="intake-stage-field"><span>Model</span><strong>{[detail.provider, detail.model_tier, detail.model].filter(Boolean).join(' / ') || '-'}</strong></div>
      <div className="intake-stage-field"><span>Status</span><strong>{titleize(detail.status)}</strong></div>
      <div className="intake-stage-field"><span>Started</span><strong>{detail.started_at ? formatDate(detail.started_at) : '-'}</strong></div>
      <div className="intake-stage-field"><span>Completed</span><strong>{detail.completed_at ? formatDate(detail.completed_at) : '-'}</strong></div>
      <div className="intake-stage-field"><span>Tokens</span><strong>{getActivityDetail([detail.prompt_tokens ? `${detail.prompt_tokens} prompt` : null, detail.completion_tokens ? `${detail.completion_tokens} completion` : null, detail.total_tokens ? `${detail.total_tokens} total` : null])}</strong></div>
      <div className="intake-stage-field"><span>Latency</span><strong>{detail.latency_ms ? `${detail.latency_ms}ms` : '-'}</strong></div>
      <div className="intake-stage-field"><span>Cost</span><strong>{detail.estimated_cost || '-'}</strong></div>
    </div>);
}



function AgentContextBlock({ label, value }) {
  const displayValue = value || 'No content recorded.';
  return (
    <section className={`intake-agent-context-block${value ? '' : ' intake-agent-context-block--empty'}`}>
      <h3>{label}</h3>
      <pre>{displayValue}</pre>
    </section>);
}



function AgentFeedbackPanel({ feedbackItems = [], loading = false, saving = false, onSubmit = async () => false, onDeactivate = () => {} }) {
  const [form, setForm] = useState({ wrong_text: '', correction_text: '' });
  const [error, setError] = useState('');
  const activeItems = feedbackItems.filter((item) => item.status === 'active');
  const inactiveItems = feedbackItems.filter((item) => item.status !== 'active');
  const canSubmit = form.wrong_text.trim() && form.correction_text.trim() && !saving;

  const updateForm =

  (field, value) => {
    setError('');
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitFeedback =

  async (event) => {
    event.preventDefault();
    if (!form.wrong_text.trim() || !form.correction_text.trim()) {
      setError('Both feedback fields are required.');
      return;
    }
    const saved = await onSubmit({
      wrong_text: form.wrong_text.trim(),
      correction_text: form.correction_text.trim()
    });
    if (saved) {
      setForm({ wrong_text: '', correction_text: '' });
    }
  };

  const renderFeedbackRows =

  (items) => items.map((item) => (
    <div className={`intake-agent-feedback-row intake-agent-feedback-row--${item.status || 'inactive'}`} key={item.id}>
      <div className="intake-agent-feedback-row__body">
        <div><span>Prior issue</span><strong>{item.wrong_text}</strong></div>
        <div><span>Correction</span><strong>{item.correction_text}</strong></div>
        <small>{[item.created_by_actor_email, item.created_at ? formatDate(item.created_at) : null].filter(Boolean).join(' / ') || 'Saved feedback'}</small>
      </div>
      <div className="intake-agent-feedback-row__actions">
        <IntakeBadge value={item.status || 'inactive'} />
        {item.status === 'active' &&
          <button type="button" className="intake-button intake-button--quiet" disabled={saving} onClick={() => onDeactivate(item.id)}>
            <AppIcon icon="pi pi-ban" aria-hidden="true" />
            <span>Deactivate</span>
          </button>}
      </div>
    </div>
  ));

  return (
    <section className="intake-agent-feedback">
      <div className="intake-agent-feedback__header">
        <div>
          <h3>Feedback Items</h3>
          <span>{activeItems.length} active / {inactiveItems.length} inactive</span>
        </div>
      </div>
      <form className="intake-agent-feedback-form" onSubmit={submitFeedback}>
        <TextArea label="What the agent got wrong" value={form.wrong_text} rows={3} disabled={saving} onChange={(value) => updateForm('wrong_text', value)} />
        <TextArea label="Correction for future runs" value={form.correction_text} rows={3} disabled={saving} onChange={(value) => updateForm('correction_text', value)} />
        {error && <div className="intake-form-error" role="alert">{error}</div>}
        <div className="intake-action-row">
          <button type="submit" className="intake-button intake-button--primary" disabled={!canSubmit}>
            <AppIcon icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} aria-hidden="true" />
            <span>Save Feedback</span>
          </button>
        </div>
      </form>
      <div className="intake-agent-feedback-list">
        {loading && <div className="intake-record-row"><span>Loading feedback...</span></div>}
        {!loading && activeItems.length > 0 && renderFeedbackRows(activeItems)}
        {!loading && inactiveItems.length > 0 && renderFeedbackRows(inactiveItems)}
        {!loading && feedbackItems.length === 0 && <EmptyState title="No feedback saved for this agent" />}
      </div>
    </section>);
}



export function AgentRunDetailModal({ detail, onClose, mode = 'full', showLegalVerification = true }) {
  const [selectedLegalSource, setSelectedLegalSource] = useState(null);
  const fullMode = mode === 'full';
  const title = fullMode ? 'Agent Run Details' : 'Agent Context';
  return (
    <>
      <AppModal
        visible={Boolean(detail)}
        title={title}
        description={detail ? `${detail.agent_display_name || detail.agent_key || `Agent #${detail.agent_id || '-'}`} / ${titleize(detail.status)}` : ''}
        onClose={onClose}
        width="min(980px, calc(100vw - 2rem))"
        className="intake-agent-run-modal">
        {detail &&
        <div className="intake-stack intake-agent-run-detail">
          <AgentRunMetadataGrid detail={detail} />
          {fullMode && detail.model_settings_json && <AgentContextBlock label="Model Settings" value={formatJsonBlock(detail.model_settings_json)} />}
          <AgentContextBlock label="Rendered System Prompt" value={detail.rendered_system_prompt || ''} />
          <AgentContextBlock label="Rendered User Prompt" value={detail.rendered_user_prompt || ''} />
          <AgentContextBlock label="Input JSON" value={formatJsonBlock(detail.input_json)} />
          {fullMode && <FormattedAgentOutput value={detail.parsed_output_json} showLegalVerification={showLegalVerification} onSourceSelect={setSelectedLegalSource} />}
          {fullMode && <AgentContextBlock label="Parsed Output JSON" value={formatJsonBlock(detail.parsed_output_json)} />}
          {fullMode && <AgentContextBlock label="Raw Response" value={formatJsonBlock(detail.raw_response)} />}
          {fullMode && detail.validation_errors_json && <AgentContextBlock label="Validation Errors" value={formatJsonBlock(detail.validation_errors_json)} />}
        </div>}
      </AppModal>
      <LegalSourceDetailModal source={selectedLegalSource} onClose={() => setSelectedLegalSource(null)} />
    </>);
}



export function AgentFeedbackModal({ target, feedbackItems = [], loading = false, saving = false, onSubmit, onDeactivate, onClose }) {
  return (
    <AppModal
      visible={Boolean(target)}
      title="Agent Feedback"
      description={target ? `${target.agent_display_name || target.agent_key || `Agent #${target.agent_id || '-'}`} / ${titleize(target.status)}` : ''}
      onClose={onClose}
      width="min(780px, calc(100vw - 2rem))"
      className="intake-agent-feedback-modal">
      {target &&
        <AgentFeedbackPanel
          feedbackItems={feedbackItems}
          loading={loading}
          saving={saving}
          onSubmit={onSubmit}
          onDeactivate={onDeactivate} />}
    </AppModal>);
}
